import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { getCode } from 'app/utils/method';

/**
 * Document Class Cell Renderer Component
 *
 * Renders the document class with:
 * - Code on the first line (bold, monospace font)
 * - Name on the second line
 *
 * Uses the getCode() utility to extract the code from either externalKeys.code or code field.
 *
 * Copied from: src/app/modules/components/monitor/table-load-list/table-load-list.component.html (lines 214-228)
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'class',
 *   cellRenderer: DocumentClassCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: 'app-document-class-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <p class="table-text-m !text-sm">
      <span *ngIf="classCode" class="table-text-code">
        {{ classCode }}<br />
      </span>
      {{ className }}
    </p>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .table-text-code {
      font-family: 'IBM Plex Mono', monospace;
      font-weight: 600;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentClassCellRendererComponent implements CellRenderer<any, any> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, any>;

  /**
   * Document class object (contains code, name, and possibly externalKeys)
   */
  get documentClass(): any {
    return this.params?.data?.class;
  }

  /**
   * Get the class code using the getCode utility
   * Extracted from externalKeys.code or code field
   */
  get classCode(): string | null {
    if (!this.documentClass) {
      return null;
    }
    return getCode(this.documentClass);
  }

  /**
   * Get the class name
   */
  get className(): string {
    return this.documentClass?.name || '';
  }
}
